import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain, Building2, Users, BookOpen, Zap, Palette,
  Plus, Edit2, Trash2, Save, X, Tag, ChevronDown,
  ChevronUp, DollarSign, AlertCircle, CheckCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';

type Tab = 'company' | 'team' | 'cases' | 'capabilities' | 'brand' | 'ratecard';

interface TeamBio {
  id: string;
  name: string;
  role: string;
  expertiseTags: string[];
  shortBio: string;
  longBio: string;
  photoUrl: string;
}

interface CaseStudy {
  id: string;
  client: string;
  industry: string;
  useCaseTags: string[];
  technologyTags: string[];
  summary: string;
  outcomesQuantified: string;
}

interface JarvisCapability {
  moduleKey: string;
  name: string;
  description: string;
  useCaseTags: string[];
}

// Real template decoded from Ryan's "GCS Onboarding Narrative and Roadmap
// Project Plan.xlsx" (Asana 1219214793218128) — one entry per
// (role, cost_type, account) combination, since rates differ per account.
// Pre-2026-10-06 rows (roleName/defaultRate/...) stay readable below but are
// no longer written — see models/RateCard.js.
interface RateCardRole {
  id: string;
  employment_type: 'Staff' | 'Contractor';
  role: string;
  examples: string;
  cost_type: 'hour' | 'day';
  cost_amount: number;
  account: string;
  bill_rate: number;
  // Legacy placeholder fields — display-only, no longer written.
  roleName?: string;
  responsibilities?: string;
  timeframeBasis?: 'hourly' | 'daily' | 'weekly';
  defaultRate?: number;
  rateMin?: number;
  rateMax?: number;
  notes?: string;
}

interface RateCardSettings {
  baseline_cost_per_week: number;
  additional_margin_pct: number;
  te_pct: number;
  te_on_own_line: boolean;
}

const DEFAULT_RATE_CARD_SETTINGS: RateCardSettings = {
  baseline_cost_per_week: 4000,
  additional_margin_pct: 0,
  te_pct: 0.04,
  te_on_own_line: true,
};

interface BrainData {
  company_profile: string;
  jarvis_capabilities: JarvisCapability[];
  ways_of_working: string;
  brand_style_guide: Record<string, string>;
  team_bios: TeamBio[];
  case_studies: CaseStudy[];
  updated_at?: string;
}

// ─── Tag Input ─────────────────────────────────────────────────────────────────
const TagInput: React.FC<{
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}> = ({ tags, onChange, placeholder }) => {
  const [input, setInput] = useState('');

  const add = () => {
    const val = input.trim();
    if (val && !tags.includes(val)) onChange([...tags, val]);
    setInput('');
  };

  return (
    <div className="border border-gray-300 rounded-lg p-2 flex flex-wrap gap-1 min-h-[44px]">
      {tags.map(tag => (
        <span key={tag} className="flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
          {tag}
          <button type="button" onClick={() => onChange(tags.filter(t => t !== tag))}>
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={tags.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[120px] outline-none text-sm"
      />
    </div>
  );
};

// ─── Team Avatar — resolves s3Key to presigned URL ──────────────────────────────
const TeamAvatar: React.FC<{ photoUrl: string; name: string; size?: number }> = ({ photoUrl, name, size = 12 }) => {
  const [src, setSrc] = useState('');

  useEffect(() => {
    if (!photoUrl) { setSrc(''); return; }
    if (photoUrl.startsWith('http') || photoUrl.startsWith('/uploads') || photoUrl.startsWith('blob:')) {
      setSrc(photoUrl);
    } else {
      api.get('/api/company-brain/team-photo', { params: { key: photoUrl } })
        .then(r => setSrc(r.data.url))
        .catch(() => setSrc(''));
    }
  }, [photoUrl]);

  const sz = `w-${size} h-${size}`;
  if (src) return <img src={src} alt={name} className={`${sz} rounded-full object-cover flex-shrink-0`} />;
  return (
    <div className={`${sz} rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0`}>
      <span className="text-white font-bold">{name?.[0] || '?'}</span>
    </div>
  );
};

// ─── Team Bio Modal ─────────────────────────────────────────────────────────────
const TeamBioModal: React.FC<{
  bio: Partial<TeamBio> | null;
  onSave: (bio: TeamBio) => void;
  onClose: () => void;
}> = ({ bio, onSave, onClose }) => {
  const [form, setForm] = useState<TeamBio>({
    id: bio?.id || `bio_${Date.now()}`,
    name: bio?.name || '',
    role: bio?.role || '',
    expertiseTags: bio?.expertiseTags || [],
    shortBio: bio?.shortBio || '',
    longBio: bio?.longBio || '',
    photoUrl: bio?.photoUrl || '',
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // On mount, resolve existing s3Key to a display URL
  useEffect(() => {
    if (bio?.photoUrl && !bio.photoUrl.startsWith('http') && !bio.photoUrl.startsWith('/uploads')) {
      api.get('/api/company-brain/team-photo', { params: { key: bio.photoUrl } })
        .then(r => setPhotoPreview(r.data.url))
        .catch(() => {});
    } else if (bio?.photoUrl) {
      setPhotoPreview(bio.photoUrl);
    }
  }, [bio?.photoUrl]);

  const handlePhotoUpload = async (file: File) => {
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await api.post('/api/company-brain/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const s3Key = res.data.s3Key;
      setForm(f => ({ ...f, photoUrl: s3Key }));
      // Fetch presigned URL to show preview only after successful upload
      const urlRes = await api.get('/api/company-brain/team-photo', { params: { key: s3Key } });
      setPhotoPreview(urlRes.data.url);
      toast.success('Photo uploaded');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{bio?.id ? 'Edit' : 'Add'} Team Member</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ryan Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                placeholder="Principal Consultant"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expertise Tags</label>
            <TagInput
              tags={form.expertiseTags}
              onChange={tags => setForm(f => ({ ...f, expertiseTags: tags }))}
              placeholder="Type tag + Enter (e.g. risk, RCSA, data science)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Short Bio (1-2 lines)</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.shortBio}
              onChange={e => setForm(f => ({ ...f, shortBio: e.target.value }))}
              placeholder="15 years in operational risk and regulatory compliance"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Bio</label>
            <textarea
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.longBio}
              onChange={e => setForm(f => ({ ...f, longBio: e.target.value }))}
              placeholder="Full bio for use in proposals and decks..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
            <div className="flex items-center gap-4">
              {/* Preview */}
              <div className="flex-shrink-0">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center border-2 border-gray-200">
                    <span className="text-white font-bold text-xl">
                      {form.name ? form.name[0].toUpperCase() : '?'}
                    </span>
                  </div>
                )}
              </div>
              {/* Upload button */}
              <div className="flex-1">
                <label className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg transition-colors text-sm font-medium ${photoUploading ? 'border-blue-300 bg-blue-50 text-blue-500 cursor-not-allowed' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-600 cursor-pointer'}`}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    className="hidden"
                    disabled={photoUploading}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) handlePhotoUpload(file);
                      e.target.value = '';
                    }}
                  />
                  {photoUploading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </span>
                  ) : (form.photoUrl ? 'Change Photo' : 'Upload Photo')}
                </label>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, SVG — max 5MB</p>
                {form.photoUrl && (
                  <button
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, photoUrl: '' })); setPhotoPreview(''); }}
                    className="text-xs text-red-500 hover:text-red-700 mt-1"
                  >
                    Remove photo
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button onClick={onClose} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button
            onClick={async () => {
              if (!form.name || !form.role) return;
              setSaving(true);
              try { await onSave(form); } finally { setSaving(false); }
            }}
            disabled={saving || !form.name || !form.role}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Case Study Modal ───────────────────────────────────────────────────────────
const CaseStudyModal: React.FC<{
  cs: Partial<CaseStudy> | null;
  onSave: (cs: CaseStudy) => void;
  onClose: () => void;
}> = ({ cs, onSave, onClose }) => {
  const [form, setForm] = useState<CaseStudy>({
    id: cs?.id || `cs_${Date.now()}`,
    client: cs?.client || '',
    industry: cs?.industry || '',
    useCaseTags: cs?.useCaseTags || [],
    technologyTags: cs?.technologyTags || [],
    summary: cs?.summary || '',
    outcomesQuantified: cs?.outcomesQuantified || '',
  });
  const [saving, setSaving] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{cs?.id ? 'Edit' : 'Add'} Case Study</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client (can be anonymized)</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.client}
                onChange={e => setForm(f => ({ ...f, client: e.target.value }))}
                placeholder="Pacific Life (or 'Major Insurer')"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.industry}
                onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                placeholder="Insurance / Financial Services"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Use Case Tags</label>
            <TagInput
              tags={form.useCaseTags}
              onChange={tags => setForm(f => ({ ...f, useCaseTags: tags }))}
              placeholder="risk, RCSA, Monte Carlo, demand forecasting..."
            />
            <p className="text-xs text-gray-500 mt-1">These tags are matched against client pain points for OU/OA deck generation</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Technology Tags</label>
            <TagInput
              tags={form.technologyTags}
              onChange={tags => setForm(f => ({ ...f, technologyTags: tags }))}
              placeholder="Python, SQL, Tableau, Jarvis..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
            <textarea
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.summary}
              onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
              placeholder="Brief description of the engagement and our approach..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantified Outcomes</label>
            <textarea
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.outcomesQuantified}
              onChange={e => setForm(f => ({ ...f, outcomesQuantified: e.target.value }))}
              placeholder="Reduced RCSA cycle time by 40%, saved $2M in compliance costs..."
            />
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button onClick={onClose} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button
            onClick={async () => {
              setSaving(true);
              try { await onSave(form); } finally { setSaving(false); }
            }}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Rate Card Role Modal ───────────────────────────────────────────────────────
const RateCardRoleModal: React.FC<{
  role: Partial<RateCardRole> | null;
  onSave: (role: RateCardRole) => void;
  onClose: () => void;
}> = ({ role, onSave, onClose }) => {
  const [form, setForm] = useState<RateCardRole>({
    id: role?.id || `role_${Date.now()}`,
    employment_type: role?.employment_type || 'Staff',
    role: role?.role || role?.roleName || '',
    examples: role?.examples || '',
    cost_type: role?.cost_type || 'hour',
    cost_amount: role?.cost_amount ?? 0,
    account: role?.account || '',
    bill_rate: role?.bill_rate ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const valid = Boolean(form.role.trim() && form.account.trim());

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{role?.id ? 'Edit' : 'Add'} Rate Card Row</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500">
            One row per (role, cost type, account) — the same role repeats once per account because rates differ by account.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                placeholder="Principal, Project Manager, ..."
              />
              <p className="text-xs text-gray-400 mt-1">Must match the plan exactly (case is ignored, spacing is not).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                value={form.employment_type}
                onChange={e => setForm(f => ({ ...f, employment_type: e.target.value as any }))}
              >
                <option value="Staff">Staff</option>
                <option value="Contractor">Contractor</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Examples</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.examples}
              onChange={e => setForm(f => ({ ...f, examples: e.target.value }))}
              placeholder="Optional — example people in this role"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.account}
                onChange={e => setForm(f => ({ ...f, account: e.target.value }))}
                placeholder="Generic, Amex, Citizens, TD, ..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost Type</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                value={form.cost_type}
                onChange={e => setForm(f => ({ ...f, cost_type: e.target.value as any }))}
              >
                <option value="hour">Hour</option>
                <option value="day">Day</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Our Cost ($ per {form.cost_type})</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.cost_amount}
                onChange={e => setForm(f => ({ ...f, cost_amount: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bill Rate ($/hour)</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={form.bill_rate}
                onChange={e => setForm(f => ({ ...f, bill_rate: parseFloat(e.target.value) || 0 }))}
              />
              <p className="text-xs text-gray-400 mt-1">Used for both hour and day billing — day billing bills 8x this rate.</p>
            </div>
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button onClick={onClose} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button
            onClick={async () => {
              if (!valid) return;
              setSaving(true);
              try { await onSave(form); } finally { setSaving(false); }
            }}
            disabled={saving || !valid}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {saving ? 'Saving...' : 'Save Row'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Company Profile Tab ────────────────────────────────────────────────────────
const CompanyProfileTab: React.FC<{
  brain: BrainData;
  setBrain: React.Dispatch<React.SetStateAction<BrainData>>;
  saving: boolean;
  onSave: (patch: { company_profile: string; ways_of_working: string }) => Promise<void>;
}> = ({ brain, setBrain, saving, onSave }) => {
  const hasContent = !!(brain.company_profile?.trim() || brain.ways_of_working?.trim());
  const [editing, setEditing] = useState(!hasContent);
  const [draft, setDraft] = useState({ company_profile: brain.company_profile, ways_of_working: brain.ways_of_working });

  const handleEdit = () => {
    setDraft({ company_profile: brain.company_profile, ways_of_working: brain.ways_of_working });
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft({ company_profile: brain.company_profile, ways_of_working: brain.ways_of_working });
    setEditing(false);
  };

  const handleSave = async () => {
    setBrain(b => ({ ...b, company_profile: draft.company_profile, ways_of_working: draft.ways_of_working }));
    await onSave({ company_profile: draft.company_profile, ways_of_working: draft.ways_of_working });
    setEditing(false);
  };

  if (!editing && hasContent) {
    return (
      <div className="space-y-4">
        {/* Company Overview — read only */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div>
              <p className="text-sm font-semibold text-gray-700">Company Overview & Core Competencies</p>
              <p className="text-xs text-gray-400 mt-0.5">Appears in the "Why TSG" slide of every OU/OA deck</p>
            </div>
            <button
              onClick={handleEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 font-medium"
            >
              <Edit2 size={13} /> Edit
            </button>
          </div>
          <div className="px-6 py-5">
            {brain.company_profile ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{brain.company_profile}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">Not filled in yet.</p>
            )}
          </div>
        </div>

        {/* Ways of Working — read only */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div>
              <p className="text-sm font-semibold text-gray-700">Ways of Working / Methodology</p>
              <p className="text-xs text-gray-400 mt-0.5">Delivery methodology and working style with clients</p>
            </div>
          </div>
          <div className="px-6 py-5">
            {brain.ways_of_working ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{brain.ways_of_working}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">Not filled in yet.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-blue-200 rounded-xl p-6 ring-1 ring-blue-100">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-700">
            Company Overview & Core Competencies
          </label>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Describe TSG — what we do, who we serve, our unique expertise and value proposition.
          This appears in the "Why TSG" slide of every OU/OA deck.
        </p>
        <textarea
          rows={10}
          autoFocus
          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          value={draft.company_profile}
          onChange={e => setDraft(d => ({ ...d, company_profile: e.target.value }))}
          placeholder="The Stemmler Group (TSG) is a boutique management consulting firm specializing in..."
        />
      </div>
      <div className="bg-white border border-blue-200 rounded-xl p-6 ring-1 ring-blue-100">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Ways of Working / Methodology
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Our delivery methodology, cadence, and working style with clients.
        </p>
        <textarea
          rows={6}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          value={draft.ways_of_working}
          onChange={e => setDraft(d => ({ ...d, ways_of_working: e.target.value }))}
          placeholder="We work in 2-week sprints with weekly client check-ins..."
        />
      </div>
      <div className="flex justify-end gap-3">
        {hasContent && (
          <button
            onClick={handleCancel}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
};

// ─── Capabilities Tab ────────────────────────────────────────────────────────────
const CapabilitiesTab: React.FC<{
  brain: BrainData;
  setBrain: React.Dispatch<React.SetStateAction<BrainData>>;
  saving: boolean;
  onSave: (caps: JarvisCapability[]) => Promise<void>;
}> = ({ brain, setBrain, saving, onSave }) => {
  const hasContent = brain.jarvis_capabilities.length > 0;
  const [editing, setEditing] = useState(!hasContent);
  const [draft, setDraft] = useState<JarvisCapability[]>(brain.jarvis_capabilities);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  // The canonical module catalog lives in the backend
  // (src/constants/jarvisCapabilities.js) and is fetched on demand rather than
  // duplicated here. A copy in this file is exactly how the catalog went stale
  // for four months: the stored data and the frontend constant drifted apart
  // with nothing able to notice.
  const loadJarvisDefaults = async () => {
    setLoadingDefaults(true);
    try {
      const res = await api.get('/api/company-brain/capability-defaults');
      const caps = res.data?.capabilities;
      if (!Array.isArray(caps) || caps.length === 0) {
        toast.error('Could not load the Jarvis default catalog.');
        return;
      }
      setDraft(caps);
      toast.success(`Loaded ${caps.length} modules — review, then Save Capabilities.`);
    } catch {
      toast.error('Could not load the Jarvis default catalog.');
    } finally {
      setLoadingDefaults(false);
    }
  };

  const moduleColors = [
    'bg-blue-50 border-blue-200 text-blue-700',
    'bg-purple-50 border-purple-200 text-purple-700',
    'bg-orange-50 border-orange-200 text-orange-700',
    'bg-emerald-50 border-emerald-200 text-emerald-700',
    'bg-rose-50 border-rose-200 text-rose-700',
    'bg-amber-50 border-amber-200 text-amber-700',
  ];

  const handleEdit = () => {
    setDraft(brain.jarvis_capabilities);
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft(brain.jarvis_capabilities);
    setEditing(false);
  };

  const handleSave = async () => {
    setBrain(b => ({ ...b, jarvis_capabilities: draft }));
    await onSave(draft);
    setEditing(false);
  };

  // Read-only view
  if (!editing && hasContent) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
          <Zap className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-800 flex-1">
            <strong>Used in BD pipeline:</strong> During OU/OA deck generation, Jarvis matches these modules to client pain points to recommend the right capabilities.
          </div>
          <button
            onClick={handleEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-100 font-medium flex-shrink-0"
          >
            <Edit2 size={13} /> Edit
          </button>
        </div>

        <div className="space-y-3">
          {brain.jarvis_capabilities.map((cap, idx) => {
            const colorClass = moduleColors[idx % moduleColors.length];
            return (
              <div key={cap.moduleKey} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className={`flex items-center gap-3 px-5 py-3 border-b ${colorClass}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClass} border`}>
                    <Zap size={14} />
                  </div>
                  <span className="flex-1 font-semibold text-sm">{cap.name || <span className="text-gray-400 italic">Unnamed module</span>}</span>
                  <span className="text-xs font-mono text-gray-400 px-2 py-0.5 bg-white rounded border border-gray-200">{cap.moduleKey}</span>
                </div>
                <div className="px-5 py-4 space-y-3">
                  {cap.description ? (
                    <p className="text-sm text-gray-700 leading-relaxed">{cap.description}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No description.</p>
                  )}
                  {cap.useCaseTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {cap.useCaseTags.map(tag => (
                        <span key={tag} className="bg-blue-50 text-blue-700 text-xs px-2.5 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
        <Zap className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-purple-800">
          <strong>Used in BD pipeline:</strong> During OU/OA deck generation, Jarvis matches these modules to client pain points to recommend the right capabilities. Keep descriptions crisp and tag-rich for best matching.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{draft.length} module{draft.length !== 1 ? 's' : ''} defined</p>
        <div className="flex gap-2">
          <button
            onClick={loadJarvisDefaults}
            disabled={loadingDefaults}
            title="Replace the draft with the current Jarvis module catalog — nothing is saved until you click Save Capabilities"
            className="flex items-center gap-2 px-4 py-2 border border-purple-300 text-purple-700 bg-purple-50 rounded-lg text-sm font-medium hover:bg-purple-100 disabled:opacity-50"
          >
            {loadingDefaults
              ? <span className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
              : <Zap size={16} />}
            {draft.length === 0 ? 'Seed with Jarvis Defaults' : 'Reset to Jarvis Defaults'}
          </button>
          <button
            onClick={() => setDraft(d => [...d, { moduleKey: `module_${Date.now()}`, name: '', description: '', useCaseTags: [] }])}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <Plus size={16} /> Add Module
          </button>
        </div>
      </div>

      {draft.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-500">
          <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No capabilities defined yet</p>
          <p className="text-sm mt-1">Click "Seed with Jarvis Defaults" to pre-populate standard modules</p>
        </div>
      ) : (
        <div className="space-y-3">
          {draft.map((cap, idx) => {
            const colorClass = moduleColors[idx % moduleColors.length];
            return (
              <div key={cap.moduleKey} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                <div className={`flex items-center gap-3 px-5 py-3 border-b ${colorClass}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClass} border`}>
                    <Zap size={14} />
                  </div>
                  <input
                    className="flex-1 bg-transparent font-semibold text-sm outline-none placeholder:text-gray-400"
                    value={cap.name}
                    onChange={e => {
                      const slug = e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                      setDraft(d => d.map((c, i) => i === idx ? { ...c, name: e.target.value, moduleKey: slug || c.moduleKey } : c));
                    }}
                    placeholder="Module name (e.g. AI Council)"
                  />
                  <span className="text-xs font-mono text-gray-400 px-2 py-0.5 bg-white rounded border border-gray-200">{cap.moduleKey}</span>
                  <button
                    onClick={() => setDraft(d => d.filter((_, i) => i !== idx))}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <textarea
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    value={cap.description}
                    onChange={e => setDraft(d => d.map((c, i) => i === idx ? { ...c, description: e.target.value } : c))}
                    placeholder="What this module does and the value it delivers..."
                  />
                  <div>
                    <p className="text-xs text-gray-500 mb-1.5">Use-case tags <span className="text-gray-400">(used for client opportunity matching)</span></p>
                    <TagInput
                      tags={cap.useCaseTags}
                      onChange={tags => setDraft(d => d.map((c, i) => i === idx ? { ...c, useCaseTags: tags } : c))}
                      placeholder="Add tag and press Enter (e.g. risk, RCSA, automation)"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end gap-3">
        {hasContent && (
          <button onClick={handleCancel} disabled={saving} className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Capabilities'}
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────────
const CompanyBrain: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [activeTab, setActiveTab] = useState<Tab>('company');
  const [brain, setBrain] = useState<BrainData>({
    company_profile: '',
    jarvis_capabilities: [],
    ways_of_working: '',
    brand_style_guide: {},
    team_bios: [],
    case_studies: [],
  });
  const [rateCardRoles, setRateCardRoles] = useState<RateCardRole[]>([]);
  const [rateCardNotes, setRateCardNotes] = useState('');
  const [rateCardSettings, setRateCardSettings] = useState<RateCardSettings>(DEFAULT_RATE_CARD_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [bioModal, setBioModal] = useState<Partial<TeamBio> | null | false>(false);
  const [csModal, setCsModal] = useState<Partial<CaseStudy> | null | false>(false);
  const [roleModal, setRoleModal] = useState<Partial<RateCardRole> | null | false>(false);
  const [expandedCs, setExpandedCs] = useState<string | null>(null);

  const fetchBrain = useCallback(async () => {
    try {
      setLoading(true);
      const brainRes = await api.get('/api/company-brain');
      setBrain(brainRes.data);
      if (isSuperAdmin) {
        const rcRes = await api.get('/api/company-brain/rate-cards');
        if (rcRes.data?.roles) {
          setRateCardRoles(rcRes.data.roles);
          const notes = rcRes.data.notes || '';
          setRateCardNotes(notes === 'No rate card configured yet' ? '' : notes);
          setRateCardSettings({
            baseline_cost_per_week: Number(rcRes.data.baseline_cost_per_week ?? DEFAULT_RATE_CARD_SETTINGS.baseline_cost_per_week),
            additional_margin_pct: Number(rcRes.data.additional_margin_pct ?? DEFAULT_RATE_CARD_SETTINGS.additional_margin_pct),
            te_pct: Number(rcRes.data.te_pct ?? DEFAULT_RATE_CARD_SETTINGS.te_pct),
            te_on_own_line: rcRes.data.te_on_own_line ?? DEFAULT_RATE_CARD_SETTINGS.te_on_own_line,
          });
        }
      }
    } catch {
      toast.error('Failed to load Company Brain');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { fetchBrain(); }, [fetchBrain]);

  const saveBrain = async (patch: Partial<BrainData>) => {
    try {
      setSaving(true);
      const res = await api.put('/api/company-brain', { ...brain, ...patch });
      setBrain(res.data);
      toast.success('Saved successfully');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveRateCard = async () => {
    try {
      setSaving(true);
      await api.put('/api/company-brain/rate-cards', {
        roles: rateCardRoles,
        notes: rateCardNotes,
        ...rateCardSettings,
      });
      toast.success('Rate card saved');
    } catch {
      toast.error('Failed to save rate card');
    } finally {
      setSaving(false);
    }
  };

  // Team Bios handlers
  const saveBio = async (bio: TeamBio) => {
    const existing = brain.team_bios.find(b => b.id === bio.id);
    const updated = existing
      ? brain.team_bios.map(b => b.id === bio.id ? bio : b)
      : [...brain.team_bios, bio];
    await saveBrain({ team_bios: updated });
    setBioModal(false);
  };

  const deleteBio = async (id: string) => {
    if (!confirm('Delete this team member?')) return;
    await saveBrain({ team_bios: brain.team_bios.filter(b => b.id !== id) });
  };

  // Case Studies handlers
  const saveCs = async (cs: CaseStudy) => {
    const existing = brain.case_studies.find(c => c.id === cs.id);
    const updated = existing
      ? brain.case_studies.map(c => c.id === cs.id ? cs : c)
      : [...brain.case_studies, cs];
    await saveBrain({ case_studies: updated });
    setCsModal(false);
  };

  const deleteCs = async (id: string) => {
    if (!confirm('Delete this case study?')) return;
    await saveBrain({ case_studies: brain.case_studies.filter(c => c.id !== id) });
  };

  // Rate card role handlers
  const saveRole = async (role: RateCardRole) => {
    const existing = rateCardRoles.find(r => r.id === role.id);
    const updatedRoles = existing
      ? rateCardRoles.map(r => r.id === role.id ? role : r)
      : [...rateCardRoles, role];
    await api.put('/api/company-brain/rate-cards', { roles: updatedRoles, notes: rateCardNotes, ...rateCardSettings });
    setRateCardRoles(updatedRoles);
    toast.success('Row saved');
    setRoleModal(false);
  };

  const deleteRole = async (id: string) => {
    if (!confirm('Remove this rate card row?')) return;
    const updatedRoles = rateCardRoles.filter(r => r.id !== id);
    await api.put('/api/company-brain/rate-cards', { roles: updatedRoles, notes: rateCardNotes, ...rateCardSettings });
    setRateCardRoles(updatedRoles);
    toast.success('Row removed');
  };

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'company', label: 'Company Profile', icon: Building2 },
    { id: 'team', label: 'Team Bios', icon: Users },
    { id: 'cases', label: 'Case Studies', icon: BookOpen },
    ...(isSuperAdmin
      ? [{ id: 'ratecard' as Tab, label: 'Rate Card', icon: DollarSign }]
      : []),
    { id: 'capabilities', label: 'Jarvis Capabilities', icon: Zap },
    { id: 'brand', label: 'Brand & Style', icon: Palette },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
          <Brain className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">TSG Bios &amp; Case Studies</h1>
          <p className="text-sm text-gray-500">
            TSG's knowledge base — case studies, team bios, and capabilities used to ground all AI-generated proposals
          </p>
        </div>
        {brain.updated_at && (
          <span className="ml-auto text-xs text-gray-400">
            Last updated {new Date(brain.updated_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Grounding notice */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          <strong>Grounding Rule:</strong> OU/OA deck generation will ONLY use case studies that match client pain point tags.
          No fabrication or interpolation. If no case studies match, none will be shown.
        </p>
      </div>

      {/* Tabs */}
      <nav className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {/* ── Company Profile ── */}
      {activeTab === 'company' && (
        <CompanyProfileTab
          brain={brain}
          setBrain={setBrain}
          saving={saving}
          onSave={(patch) => saveBrain(patch)}
        />
      )}

      {/* ── Team Bios ── */}
      {activeTab === 'team' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {brain.team_bios.length} team member{brain.team_bios.length !== 1 ? 's' : ''} — bios are matched to engagements by expertise tags
            </p>
            <button
              onClick={() => setBioModal({})}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus size={16} /> Add Team Member
            </button>
          </div>
          {brain.team_bios.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-500">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No team members yet</p>
              <p className="text-sm mt-1">Add bios to include team slides in OU/OA decks</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {brain.team_bios.map(bio => (
                <div key={bio.id} className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4">
                  <TeamAvatar photoUrl={bio.photoUrl} name={bio.name} size={12} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">{bio.name}</span>
                      <span className="text-sm text-gray-500">— {bio.role}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{bio.shortBio}</p>
                    <div className="flex flex-wrap gap-1">
                      {bio.expertiseTags.map(tag => (
                        <span key={tag} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Tag size={10} /> {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setBioModal(bio)} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => deleteBio(bio.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Case Studies ── */}
      {activeTab === 'cases' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {brain.case_studies.length} case stud{brain.case_studies.length !== 1 ? 'ies' : 'y'} — matched by tags to client pain points
            </p>
            <button
              onClick={() => setCsModal({})}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus size={16} /> Add Case Study
            </button>
          </div>
          {brain.case_studies.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-500">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No case studies yet</p>
              <p className="text-sm mt-1">Add case studies to be matched into OU/OA decks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {brain.case_studies.map(cs => (
                <div key={cs.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedCs(expandedCs === cs.id ? null : cs.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{cs.client || 'Unnamed Client'}</span>
                        {cs.industry && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{cs.industry}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {cs.useCaseTags.slice(0, 4).map(tag => (
                          <span key={tag} className="bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                        {cs.useCaseTags.length > 4 && (
                          <span className="text-xs text-gray-400">+{cs.useCaseTags.length - 4} more</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={e => { e.stopPropagation(); setCsModal(cs); }} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={e => { e.stopPropagation(); deleteCs(cs.id); }} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                        <Trash2 size={16} />
                      </button>
                      {expandedCs === cs.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </div>
                  {expandedCs === cs.id && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                      {cs.summary && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Summary</p>
                          <p className="text-sm text-gray-700">{cs.summary}</p>
                        </div>
                      )}
                      {cs.outcomesQuantified && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Quantified Outcomes</p>
                          <p className="text-sm text-gray-700 font-medium text-green-700">{cs.outcomesQuantified}</p>
                        </div>
                      )}
                      {cs.technologyTags.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Technology</p>
                          <div className="flex flex-wrap gap-1">
                            {cs.technologyTags.map(tag => (
                              <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Jarvis Capabilities ── */}
      {activeTab === 'capabilities' && (
        <CapabilitiesTab
          brain={brain}
          setBrain={setBrain}
          saving={saving}
          onSave={(caps) => saveBrain({ jarvis_capabilities: caps })}
        />
      )}

      {/* ── Brand & Style ── */}
      {activeTab === 'brand' && (
        <div className="space-y-6">
          {/* Context banner */}
          <div className="flex items-start gap-3 bg-pink-50 border border-pink-200 rounded-lg p-4">
            <Palette className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-pink-800">
              <strong>Used in BD pipeline:</strong> Primary and secondary colors are injected into OU/OA deck slides. Voice & tone guides AI-generated SOW language. Template references tell Jarvis which master deck/doc to build upon.
            </div>
          </div>

          {/* Live color preview */}
          {(brain.brand_style_guide.primaryColor || brain.brand_style_guide.secondaryColor) && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Brand Preview</p>
              <div className="flex items-stretch gap-4">
                {/* Mock slide preview */}
                <div className="flex-1 rounded-lg overflow-hidden border border-gray-200 shadow-sm" style={{ minHeight: 96 }}>
                  <div
                    className="h-8 flex items-center px-4"
                    style={{ backgroundColor: brain.brand_style_guide.primaryColor || '#003087' }}
                  >
                    <div className="text-white text-xs font-semibold opacity-80">Slide Header</div>
                  </div>
                  <div className="bg-white px-4 py-3 flex-1">
                    <div className="flex gap-2 mb-2">
                      <div className="h-2 rounded-full flex-1" style={{ backgroundColor: brain.brand_style_guide.primaryColor || '#003087', opacity: 0.15 }} />
                      <div className="h-2 rounded-full w-1/3" style={{ backgroundColor: brain.brand_style_guide.secondaryColor || '#57C8F4', opacity: 0.25 }} />
                    </div>
                    <div className="h-1.5 rounded-full w-full bg-gray-100 mb-1.5" />
                    <div className="h-1.5 rounded-full w-3/4 bg-gray-100" />
                  </div>
                  <div
                    className="h-2"
                    style={{ backgroundColor: brain.brand_style_guide.secondaryColor || '#57C8F4' }}
                  />
                </div>
                {/* Swatch legend */}
                <div className="flex flex-col gap-3 justify-center min-w-[160px]">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg border border-gray-200 shadow-sm flex-shrink-0"
                      style={{ backgroundColor: brain.brand_style_guide.primaryColor || '#e5e7eb' }}
                    />
                    <div>
                      <p className="text-xs font-medium text-gray-700">Primary</p>
                      <p className="text-xs text-gray-400 font-mono">{brain.brand_style_guide.primaryColor || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg border border-gray-200 shadow-sm flex-shrink-0"
                      style={{ backgroundColor: brain.brand_style_guide.secondaryColor || '#e5e7eb' }}
                    />
                    <div>
                      <p className="text-xs font-medium text-gray-700">Secondary</p>
                      <p className="text-xs text-gray-400 font-mono">{brain.brand_style_guide.secondaryColor || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Colors & Fonts */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Brand Colors & Fonts</h3>
            <div className="grid grid-cols-2 gap-5">
              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Primary Color</label>
                <div className="flex gap-2">
                  <div className="relative flex-shrink-0">
                    <input
                      type="color"
                      className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                      value={/^#[0-9A-Fa-f]{6}$/.test(brain.brand_style_guide.primaryColor || '') ? brain.brand_style_guide.primaryColor : '#003087'}
                      onChange={e => setBrain(b => ({
                        ...b,
                        brand_style_guide: { ...b.brand_style_guide, primaryColor: e.target.value },
                      }))}
                    />
                  </div>
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500"
                    value={brain.brand_style_guide.primaryColor || ''}
                    onChange={e => setBrain(b => ({
                      ...b,
                      brand_style_guide: { ...b.brand_style_guide, primaryColor: e.target.value },
                    }))}
                    placeholder="#003087"
                  />
                </div>
              </div>
              {/* Secondary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Secondary Color</label>
                <div className="flex gap-2">
                  <div className="relative flex-shrink-0">
                    <input
                      type="color"
                      className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                      value={/^#[0-9A-Fa-f]{6}$/.test(brain.brand_style_guide.secondaryColor || '') ? brain.brand_style_guide.secondaryColor : '#57C8F4'}
                      onChange={e => setBrain(b => ({
                        ...b,
                        brand_style_guide: { ...b.brand_style_guide, secondaryColor: e.target.value },
                      }))}
                    />
                  </div>
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500"
                    value={brain.brand_style_guide.secondaryColor || ''}
                    onChange={e => setBrain(b => ({
                      ...b,
                      brand_style_guide: { ...b.brand_style_guide, secondaryColor: e.target.value },
                    }))}
                    placeholder="#57C8F4"
                  />
                </div>
              </div>
              {/* Fonts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Fonts</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={brain.brand_style_guide.fonts || ''}
                  onChange={e => setBrain(b => ({
                    ...b,
                    brand_style_guide: { ...b.brand_style_guide, fonts: e.target.value },
                  }))}
                  placeholder="Calibri, Arial"
                />
                <p className="text-xs text-gray-400 mt-1">Used as font guidance in OU/OA deck prompt</p>
              </div>
              {/* Voice & Tone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Voice & Tone</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={brain.brand_style_guide.voiceTone || ''}
                  onChange={e => setBrain(b => ({
                    ...b,
                    brand_style_guide: { ...b.brand_style_guide, voiceTone: e.target.value },
                  }))}
                  placeholder="Professional, direct, data-driven"
                />
                <p className="text-xs text-gray-400 mt-1">Injected into SOW generation prompt</p>
              </div>
            </div>
          </div>

          {/* Template References */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Template References</h3>
            <p className="text-xs text-gray-400 mb-4">File keys or paths that Jarvis uses as the base template when generating decks and SOWs</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PowerPoint Template</label>
                <div className="flex gap-2">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-50 border border-orange-200 flex items-center justify-center">
                    <span className="text-xs font-bold text-orange-600">PPT</span>
                  </div>
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    value={brain.brand_style_guide.pptTemplateRef || ''}
                    onChange={e => setBrain(b => ({
                      ...b,
                      brand_style_guide: { ...b.brand_style_guide, pptTemplateRef: e.target.value },
                    }))}
                    placeholder="TSG_standard_deck_v2 / S3 key / SharePoint URL"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Word/SOW Template</label>
                <div className="flex gap-2">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600">DOC</span>
                  </div>
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    value={brain.brand_style_guide.wordTemplateRef || ''}
                    onChange={e => setBrain(b => ({
                      ...b,
                      brand_style_guide: { ...b.brand_style_guide, wordTemplateRef: e.target.value },
                    }))}
                    placeholder="TSG_SOW_template_v3 / S3 key / SharePoint URL"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => saveBrain({ brand_style_guide: brain.brand_style_guide })}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save Brand Settings'}
            </button>
          </div>
        </div>
      )}

      {/* ── Rate Card ── */}
      {activeTab === 'ratecard' && !isSuperAdmin && (
        <AccessDenied
          requiredRole="SUPER_ADMIN"
          message="The Rate Card contains confidential pricing information and is restricted to Super Admin users."
        />
      )}
      {activeTab === 'ratecard' && isSuperAdmin && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Rates differ per account — add one row per (role, cost type, account) combination. These rows and the
              settings below drive the BD Pipeline pricing engine and Excel model export.
            </p>
          </div>

          {/* ── Engagement-level pricing settings ── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Pricing Settings</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Baseline Cost / Week ($)</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={rateCardSettings.baseline_cost_per_week}
                  onChange={e => setRateCardSettings(s => ({ ...s, baseline_cost_per_week: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Additional Margin (%)</label>
                <input
                  type="number"
                  step="0.1"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={rateCardSettings.additional_margin_pct * 100}
                  onChange={e => setRateCardSettings(s => ({ ...s, additional_margin_pct: (parseFloat(e.target.value) || 0) / 100 }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">T&amp;E Allowance (%)</label>
                <input
                  type="number"
                  step="0.1"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={rateCardSettings.te_pct * 100}
                  onChange={e => setRateCardSettings(s => ({ ...s, te_pct: (parseFloat(e.target.value) || 0) / 100 }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">T&amp;E on its own SOW line?</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={rateCardSettings.te_on_own_line ? 'yes' : 'no'}
                  onChange={e => setRateCardSettings(s => ({ ...s, te_on_own_line: e.target.value === 'yes' }))}
                >
                  <option value="yes">Yes — its own line</option>
                  <option value="no">No — folded into hours</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              The $4,000/week baseline is folded into every billable resource's SOW hours pro-rata — it never appears
              as its own line item. When T&amp;E is not on its own line, it inflates every resource's hours instead.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {rateCardRoles.length} row{rateCardRoles.length !== 1 ? 's' : ''} defined
            </p>
            <button
              onClick={() => setRoleModal({})}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus size={16} /> Add Row
            </button>
          </div>
          {rateCardRoles.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-500">
              <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No rate card rows defined yet</p>
              <p className="text-sm mt-1">Add rows to enable project plan pricing</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Employment</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Account</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Cost Type</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Our Cost</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Bill Rate</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rateCardRoles.map(row => {
                    const isLegacy = !row.role && !!row.roleName;
                    return (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{row.role || row.roleName}</div>
                          {isLegacy && <div className="text-xs text-amber-600">Legacy row — edit to migrate to the new shape</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{row.employment_type || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{row.account || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 capitalize">{row.cost_type || row.timeframeBasis || '—'}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          {isLegacy ? `$${(row.defaultRate || 0).toLocaleString()}` : `$${(row.cost_amount || 0).toLocaleString()}`}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500">{isLegacy ? '—' : `$${(row.bill_rate || 0).toLocaleString()}`}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => setRoleModal(row)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => deleteRole(row.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={rateCardNotes}
              onChange={e => setRateCardNotes(e.target.value)}
              placeholder="Rate card notes or version info..."
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={saveRateCard}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving
                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Save size={16} />}
              {saving ? 'Saving...' : 'Save Rate Card'}
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {bioModal !== false && (
        <TeamBioModal bio={bioModal} onSave={saveBio} onClose={() => setBioModal(false)} />
      )}
      {csModal !== false && (
        <CaseStudyModal cs={csModal} onSave={saveCs} onClose={() => setCsModal(false)} />
      )}
      {roleModal !== false && (
        <RateCardRoleModal role={roleModal} onSave={saveRole} onClose={() => setRoleModal(false)} />
      )}
    </div>
  );
};

export default CompanyBrain;
